"use strict";
// Copyright (c) 2025 MDFriday. All rights reserved.
// This software is proprietary and confidential.
// Use of this source code is governed by the license that can be found in the LICENSE file.
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tree = exports.Mode = void 0;
exports.New = New;
exports.parse = parse;
exports.IsEmptyTree = IsEmptyTree;
// Package template implements data-driven templates for generating textual output.
// This is the TypeScript implementation of the Go template package.
const node_1 = require("./node");
const lex_1 = require("./lex");
// Mode values for the Template.
var Mode;
(function (Mode) {
    Mode[Mode["ParseComments"] = 1] = "ParseComments";
    Mode[Mode["SkipFuncCheck"] = 2] = "SkipFuncCheck"; // do not check that functions are defined
})(Mode || (exports.Mode = Mode = {}));
// New allocates a new parse tree with the given name.
function New(name, funcs = []) {
    return new Tree(name, funcs);
}
// Tree is the representation of a single parsed template.
class Tree {
    constructor(name, funcs = []) {
        this.Name = name;
        this.ParseName = "";
        this.Root = null;
        this.Mode = 0; // 默认不设置 ParseComments 模式，与 Go 版本保持一致
        this.text = "";
        this.funcs = funcs;
        this.lex = null;
        this.tokenArray = new Array(3).fill(null);
        this.peekCount = 0;
        this.vars = [];
        this.treeSet = new Map();
        this.actionLine = 0;
        this.rangeDepth = 0;
    }
    // Copy returns a copy of the Tree. Any parsing state is discarded.
    copy() {
        var _a;
        if (!this) {
            return null;
        }
        return new Tree(this.Name, []).setProperties({
            ParseName: this.ParseName,
            Root: (_a = this.Root) === null || _a === void 0 ? void 0 : _a.CopyList(),
            text: this.text
        });
    }
    setProperties(props) {
        Object.assign(this, props);
        return this;
    }
    // next returns the next token.
    next() {
        if (this.peekCount > 0) {
            this.peekCount--;
        }
        else {
            if (!this.lex) {
                throw new Error("Lexer not initialized");
            }
            const nextItem = this.lex.nextItem();
            if (!nextItem) {
                throw new Error("No token available from lexer");
            }
            this.tokenArray[0] = nextItem;
        }
        const nextToken = this.tokenArray[this.peekCount];
        if (!nextToken) {
            throw new Error("No token available");
        }
        return nextToken;
    }
    // backup backs the input stream up one token.
    backup() {
        this.peekCount++;
    }
    // backup2 backs the input stream up two tokens.
    // The zeroth token is already there.
    backup2(t1) {
        this.tokenArray[1] = t1;
        this.peekCount = 2;
    }
    // backup3 backs the input stream up three tokens
    // The zeroth token is already there.
    backup3(t2, t1) {
        this.tokenArray[1] = t1;
        this.tokenArray[2] = t2;
        this.peekCount = 3;
    }
    // peek returns but does not consume the next token.
    peek() {
        if (this.peekCount > 0) {
            const token = this.tokenArray[this.peekCount - 1];
            if (!token) {
                throw new Error("No token available");
            }
            return token;
        }
        this.peekCount = 1;
        if (!this.lex) {
            throw new Error("Lexer not initialized");
        }
        const nextItem = this.lex.nextItem();
        if (!nextItem) {
            throw new Error("No token available from lexer");
        }
        this.tokenArray[0] = nextItem;
        return nextItem;
    }
    // nextNonSpace returns the next non-space token.
    nextNonSpace() {
        let token;
        for (;;) {
            token = this.next();
            if (token.typ !== lex_1.ItemType.itemSpace) {
                break;
            }
        }
        return token;
    }
    // peekNonSpace returns but does not consume the next non-space token.
    peekNonSpace() {
        const token = this.nextNonSpace();
        this.backup();
        return token;
    }
    // ErrorContext returns a textual representation of the location of the node in the input text.
    errorContext(node) {
        const pos = node.Position();
        const tree = node.tree() || this;
        const text = tree.text.substring(0, pos);
        let byteNum = text.lastIndexOf('\n');
        if (byteNum === -1) {
            byteNum = pos; // On first line.
        }
        else {
            byteNum++; // After the newline.
            byteNum = pos - byteNum;
        }
        const lineNum = 1 + (text.match(/\n/g) || []).length;
        const context = node.toString();
        return [`${tree.ParseName}:${lineNum}:${byteNum}`, context];
    }
    // errorf formats the error and terminates processing.
    errorf(format, ...args) {
        this.Root = null;
        const token = this.tokenArray[0];
        if (!token) {
            throw new Error(`template: ${this.ParseName}: ${(0, node_1.sprintf)(format, ...args)}`);
        }
        format = `template: ${this.ParseName}:${token.line}: ${format}`;
        throw new Error((0, node_1.sprintf)(format, ...args));
    }
    // error terminates processing.
    error(err) {
        this.errorf("%s", err);
    }
    // expect consumes the next token and guarantees it has the required type.
    expect(expected, context) {
        const token = this.nextNonSpace();
        if (token.typ !== expected) {
            this.unexpected(token, context);
        }
        return token;
    }
    // expectOneOf consumes the next token and guarantees it has one of the required types.
    expectOneOf(expected1, expected2, context) {
        const token = this.nextNonSpace();
        if (token.typ !== expected1 && token.typ !== expected2) {
            this.unexpected(token, context);
        }
        return token;
    }
    // unexpected complains about the token and terminates processing.
    unexpected(token, context) {
        if (token.typ === lex_1.ItemType.itemError) {
            let extra = '';
            if (this.actionLine !== 0 && this.actionLine !== token.line) {
                extra = ` in action started at ${this.ParseName}:${this.actionLine}`;
                if (token.val.endsWith(' action')) {
                    extra = extra.substring(' in action'.length); // avoid "action in action"
                }
            }
            this.errorf('%s%s', token.val, extra);
        }
        this.errorf('unexpected %s in %s', token.val, context);
    }
    // recover is the handler that turns panics into returns from the top level of Parse.
    recover(errp) {
        var _a;
        const e = errp.err;
        if (e) {
            if (e instanceof Error && ((_a = e.stack) === null || _a === void 0 ? void 0 : _a.includes('runtime error:'))) {
                throw e;
            }
            if (this) {
                this.stopParse();
            }
            errp.err = e;
        }
    }
    // Node creation methods
    newList(pos) {
        return new node_1.ListNode(pos).setTree(this);
    }
    newText(pos, text) {
        return new node_1.TextNode(this, pos, text);
    }
    newAction(pos, line, pipeline) {
        if (!(pipeline instanceof node_1.PipeNode)) {
            throw new Error("Pipeline must be a PipeNode");
        }
        return new node_1.ActionNode(pos, line, pipeline).setTree(this);
    }
    newComment(pos, text) {
        return new node_1.CommentNode(pos, text).setTree(this);
    }
    newIf(pos, line, pipe, list, elseList) {
        if (!(pipe instanceof node_1.PipeNode)) {
            throw new Error("Pipe must be a PipeNode");
        }
        return new node_1.IfNode(this, pos, line, pipe, list, elseList);
    }
    newRange(pos, line, pipe, list, elseList) {
        if (!(pipe instanceof node_1.PipeNode)) {
            throw new Error("Pipe must be a PipeNode");
        }
        return new node_1.RangeNode(this, pos, line, pipe, list, elseList);
    }
    newTemplate(pos, line, name, pipe) {
        if (pipe !== null && !(pipe instanceof node_1.PipeNode)) {
            throw new Error("Pipe must be a PipeNode");
        }
        return new node_1.TemplateNode(pos, line, name, pipe).setTree(this);
    }
    newWith(pos, line, pipe, list, elseList) {
        if (!(pipe instanceof node_1.PipeNode)) {
            throw new Error("Pipe must be a PipeNode");
        }
        return new node_1.WithNode(this, pos, line, pipe, list, elseList);
    }
    newPipeline(pos, line, vars = []) {
        const pipe = new node_1.PipeNode(pos, line).setTree(this);
        if (vars) {
            pipe.Decl = vars;
        }
        return pipe;
    }
    newCommand(pos) {
        return new node_1.CommandNode(pos).setTree(this);
    }
    newChain(pos, node) {
        return new node_1.ChainNode(pos, node).setTree(this);
    }
    // textOrAction:
    //  text | comment | action
    textOrAction() {
        const token = this.nextNonSpace();
        switch (token.typ) {
            case lex_1.ItemType.itemText:
                return this.newText(token.pos, token.val);
            case lex_1.ItemType.itemLeftDelim:
                this.actionLine = token.line;
                const result = this.action();
                this.clearActionLine();
                return result;
            case lex_1.ItemType.itemComment:
                return this.newComment(token.pos, token.val);
            default:
                this.unexpected(token, "input");
                return this.newText(token.pos, ""); // Unreachable
        }
    }
    // Action:
    //  control
    //  command ("|" command)*
    //
    // Left delim is past. Now get actions.
    // First word could be a keyword such as range.
    action() {
        const token = this.nextNonSpace();
        this.actionLine = token.line;
        switch (token.typ) {
            case lex_1.ItemType.itemBlock:
                return this.blockControl();
            case lex_1.ItemType.itemBreak:
                return this.breakControl(token.pos, token.line);
            case lex_1.ItemType.itemContinue:
                return this.continueControl(token.pos, token.line);
            case lex_1.ItemType.itemElse:
                return this.elseControl();
            case lex_1.ItemType.itemEnd:
                return this.endControl();
            case lex_1.ItemType.itemIf:
                return this.ifControl();
            case lex_1.ItemType.itemRange:
                return this.rangeControl();
            case lex_1.ItemType.itemTemplate:
                return this.templateControl();
            case lex_1.ItemType.itemWith:
                return this.withControl();
        }
        this.backup();
        const pos = this.peek().pos;
        const line = this.peek().line;
        // Do not pop variables; they persist until "end".
        const pipe = this.pipeline("command", lex_1.ItemType.itemRightDelim);
        return this.newAction(pos, line, pipe);
    }
    // parse is the top-level parser for a template.
    // It runs to EOF and returns the parsed representation.
    // It is called by Parse but also by the Parse method of related templates.
    parse(text, leftDelim, rightDelim, treeSet, funcs = []) {
        this.ParseName = this.Name;
        const lexer = (0, lex_1.lex)(this.Name, text, leftDelim, rightDelim);
        this.startParse(funcs, lexer, treeSet);
        this.text = text;
        let err = null;
        try {
            this.doParse();
            this.add();
            return [this, null];
        }
        catch (e) {
            err = e instanceof Error ? e : new Error(String(e));
            return [this, err];
        }
        finally {
            this.stopParse();
        }
    }
    // startParse initializes the parser, using the lexer.
    startParse(funcs, lexer, treeSet) {
        this.Root = null;
        this.lex = lexer;
        this.funcs = funcs;
        this.treeSet = treeSet;
        this.tokenArray = new Array(3).fill(null);
        this.peekCount = 0;
        this.vars = ["$"];
        this.actionLine = 0;
        // 设置lexer的options
        if (this.lex) {
            this.lex.options = {
                emitComment: (this.Mode & Mode.ParseComments) !== 0,
                breakOK: !this.hasFunction("break"),
                continueOK: !this.hasFunction("continue")
            };
        }
    }
    // stopParse terminates parsing.
    stopParse() {
        this.lex = null;
        this.funcs = [];
        this.treeSet = new Map();
        this.tokenArray = new Array(3).fill(null);
        this.peekCount = 0;
        this.vars = [];
        this.actionLine = 0;
    }
    // doParse is the helper to parse, which runs to EOF.
    doParse() {
        this.Root = this.newList(this.peek().pos);
        while (this.peek().typ !== lex_1.ItemType.itemEOF) {
            // 检查是否是定义
            if (this.peek().typ === lex_1.ItemType.itemLeftDelim) {
                const delim = this.next();
                if (this.nextNonSpace().typ === lex_1.ItemType.itemDefine) {
                    const newT = New("definition"); // name will be updated once we know it.
                    newT.text = this.text;
                    newT.Mode = this.Mode;
                    newT.ParseName = this.ParseName;
                    newT.startParse(this.funcs, this.lex, this.treeSet);
                    newT.parseDefinition();
                    continue;
                }
                this.backup2(delim);
            }
            const n = this.textOrAction();
            switch (n.Type()) {
                case node_1.NodeType.NodeEnd:
                    this.errorf("unexpected %s", n);
                    break;
                case node_1.NodeType.NodeElse:
                    this.errorf("unexpected %s", n);
                    break;
                default:
                    this.Root.append(n);
            }
        }
    }
    // parseDefinition parses a {{define}} ...  {{end}} template definition and
    // installs the definition in the treeSet map.  The "define" keyword has already
    // been scanned.
    parseDefinition() {
        const context = "define clause";
        const name = this.expectOneOf(lex_1.ItemType.itemString, lex_1.ItemType.itemRawString, context);
        const nameStr = this.unquote(name.val);
        if (nameStr instanceof Error) {
            this.error(nameStr);
        }
        this.Name = nameStr;
        this.expect(lex_1.ItemType.itemRightDelim, context);
        let end;
        [this.Root, end] = this.itemList();
        if (end.Type() !== node_1.NodeType.NodeEnd) {
            this.errorf("unexpected %s in %s", end, context);
        }
        this.add();
        this.stopParse();
    }
    // Break:
    //  {{break}}
    //
    // Break keyword is past.
    breakControl(pos, line) {
        const token = this.tokenArray[0];
        if (!token) {
            throw new Error("No token available");
        }
        if (this.nextNonSpace().typ !== lex_1.ItemType.itemRightDelim) {
            this.unexpected(token, "{{break}}");
        }
        if (this.rangeDepth === 0) {
            this.errorf("{{break}} outside of range");
        }
        return this.newBreak(pos, line);
    }
    // Continue:
    //  {{continue}}
    //
    // Continue keyword is past.
    continueControl(pos, line) {
        const token = this.tokenArray[0];
        if (!token) {
            throw new Error("No token available");
        }
        if (this.nextNonSpace().typ !== lex_1.ItemType.itemRightDelim) {
            this.unexpected(token, "{{continue}}");
        }
        if (this.rangeDepth === 0) {
            this.errorf("{{continue}} outside of range");
        }
        return this.newContinue(pos, line);
    }
    // End:
    //  {{end}}
    //
    // End keyword is past.
    endControl() {
        return new node_1.EndNode(this.expect(lex_1.ItemType.itemRightDelim, "end").pos).setTree(this);
    }
    // Else:
    //  {{else}}
    //
    // Else keyword is past.
    elseControl() {
        // Special case for "else if".
        const peek = this.peekNonSpace();
        if (peek.typ === lex_1.ItemType.itemIf) {
            // We see "{{else if ... " but in effect rewrite it to {{else}}{{if ... ".
            return new node_1.ElseNode(peek.pos, peek.line).setTree(this);
        }
        const token = this.expect(lex_1.ItemType.itemRightDelim, "else");
        return new node_1.ElseNode(token.pos, token.line).setTree(this);
    }
    // If:
    //  {{if pipeline}} itemList {{end}}
    //  {{if pipeline}} itemList {{else}} itemList {{end}}
    //
    // If keyword is past.
    ifControl() {
        const [pos, line, pipe, list, elseList] = this.parseControl(true, "if");
        return this.newIf(pos, line, pipe, list, elseList);
    }
    // Range:
    //  {{range pipeline}} itemList {{end}}
    //  {{range pipeline}} itemList {{else}} itemList {{end}}
    //
    // Range keyword is past.
    rangeControl() {
        const [pos, line, pipe, list, elseList] = this.parseControl(false, "range");
        return this.newRange(pos, line, pipe, list, elseList);
    }
    // With:
    //  {{with pipeline}} itemList {{end}}
    //  {{with pipeline}} itemList {{else}} itemList {{end}}
    //
    // If keyword is past.
    withControl() {
        const [pos, line, pipe, list, elseList] = this.parseControl(false, "with");
        return this.newWith(pos, line, pipe, list, elseList);
    }
    // Template:
    //  {{template stringValue pipeline}}
    //
    // Template keyword is past. The name must be something that can evaluate
    // to a string.
    templateControl() {
        const context = "template clause";
        const token = this.nextNonSpace();
        const name = this.parseTemplateName(token, context);
        let pipe = null;
        if (this.nextNonSpace().typ !== lex_1.ItemType.itemRightDelim) {
            this.backup();
            // Do not pop variables; they persist until "end".
            pipe = this.pipeline(context, lex_1.ItemType.itemRightDelim);
        }
        return this.newTemplate(token.pos, token.line, name, pipe);
    }
    // Block:
    //  {{block stringValue pipeline}}
    //
    // Block keyword is past.
    // The name must be something that can evaluate to a string.
    // The pipeline is mandatory.
    blockControl() {
        const context = "block clause";
        const token = this.nextNonSpace();
        const name = this.parseTemplateName(token, context);
        const pipe = this.pipeline(context, lex_1.ItemType.itemRightDelim);
        // 由于 lex.ts 中没有 clone 方法，我们需要保存当前状态而不是克隆 lexer
        const savedLex = this.lex;
        const savedTokenArray = [...this.tokenArray];
        const savedPeekCount = this.peekCount;
        if (!savedLex) {
            this.errorf("Failed to save lexer state");
        }
        // 创建一个新的 Tree 来解析块
        const block = new Tree(name);
        block.Mode = this.Mode;
        block.Root = this.newList(token.pos);
        block.ParseName = this.ParseName;
        block.startParse(this.funcs, savedLex, this.treeSet);
        let end;
        [block.Root, end] = block.itemList();
        if (end.Type() !== node_1.NodeType.NodeEnd) {
            this.errorf("unexpected %s in %s", end.toString(), context);
        }
        block.add();
        // 恢复原始状态
        this.lex = savedLex;
        this.tokenArray = savedTokenArray;
        this.peekCount = savedPeekCount;
        return this.newTemplate(token.pos, token.line, name, pipe);
    }
    // itemList:
    // ( simple_or_next_item )*
    // Terminates at {{end}} or {{else}}, returned separately.
    itemList() {
        const list = this.newList(this.peekNonSpace().pos);
        while (this.peekNonSpace().typ !== lex_1.ItemType.itemEOF) {
            const n = this.textOrAction();
            switch (n.Type()) {
                case node_1.NodeType.NodeEnd:
                case node_1.NodeType.NodeElse:
                    return [list, n];
            }
            list.append(n);
        }
        this.errorf("unexpected EOF");
        return [list, this.newEnd(0)]; // 不会执行到这里
    }
    // parseTemplateName parses a template name from the input.
    parseTemplateName(token, context) {
        switch (token.typ) {
            case lex_1.ItemType.itemString:
            case lex_1.ItemType.itemRawString:
                const s = this.unquote(token.val);
                if (s instanceof Error) {
                    this.error(s);
                }
                return s;
            default:
                this.unexpected(token, context);
        }
        return ""; // Never reached
    }
    // popVars trims the variable list to the specified length
    popVars(n) {
        this.vars = this.vars.slice(0, n);
    }
    // Pipeline:
    //  declarations? command ('|' command)*
    pipeline(context, end) {
        let token = this.peekNonSpace();
        const pipe = this.newPipeline(token.pos, token.line);
        // Are there declarations or assignments?
        let processDeclaration = true;
        while (processDeclaration && this.peekNonSpace().typ === lex_1.ItemType.itemVariable) {
            let tokenVar = this.next();
            // Since space is a token, we need 3-token look-ahead here in the worst case:
            // in "$x foo" we need to read "foo" (as opposed to ":=") to know that $x is an
            // argument variable rather than a declaration. So remember the token
            // adjacent to the variable so we can push it back if necessary.
            const tokenAfterVariable = this.peek();
            const next = this.peekNonSpace();
            switch (true) {
                case next.typ === lex_1.ItemType.itemAssign || next.typ === lex_1.ItemType.itemDeclare:
                    pipe.IsAssign = next.typ === lex_1.ItemType.itemAssign;
                    this.nextNonSpace();
                    pipe.Decl.push(this.newVariable(tokenVar.pos, tokenVar.val));
                    this.vars.push(tokenVar.val);
                    processDeclaration = false; // Done with declarations
                    break;
                case next.typ === lex_1.ItemType.itemChar && next.val === ",":
                    this.nextNonSpace();
                    pipe.Decl.push(this.newVariable(tokenVar.pos, tokenVar.val));
                    this.vars.push(tokenVar.val);
                    if (context === "range" && pipe.Decl.length < 2) {
                        switch (this.peekNonSpace().typ) {
                            case lex_1.ItemType.itemVariable:
                                // second initialized variable in a range pipeline
                                // In Go this would be "goto decls", in TypeScript we'll just continue the loop
                                // Continue the loop to process the next variable
                                break;
                            case lex_1.ItemType.itemRightDelim:
                            case lex_1.ItemType.itemRightParen:
                                // No second variable, just exit the loop
                                processDeclaration = false;
                                break;
                            default:
                                this.errorf("range can only initialize variables");
                        }
                    }
                    else {
                        // This error should only be reached if we have too many declarations
                        this.errorf("too many declarations in %s", context);
                    }
                    break;
                case tokenAfterVariable.typ === lex_1.ItemType.itemSpace:
                    this.backup3(tokenVar, tokenAfterVariable);
                    processDeclaration = false; // Done with declarations
                    break;
                default:
                    this.backup2(tokenVar);
                    processDeclaration = false; // Done with declarations
                    break;
            }
        }
        for (;;) {
            const token = this.nextNonSpace();
            switch (token.typ) {
                case end:
                    // At this point, the pipeline is complete
                    this.checkPipeline(pipe, context);
                    return pipe;
                case lex_1.ItemType.itemBool:
                case lex_1.ItemType.itemCharConstant:
                case lex_1.ItemType.itemComplex:
                case lex_1.ItemType.itemDot:
                case lex_1.ItemType.itemField:
                case lex_1.ItemType.itemIdentifier:
                case lex_1.ItemType.itemNumber:
                case lex_1.ItemType.itemNil:
                case lex_1.ItemType.itemRawString:
                case lex_1.ItemType.itemString:
                case lex_1.ItemType.itemVariable:
                case lex_1.ItemType.itemLeftParen:
                    this.backup();
                    pipe.append(this.command());
                    break;
                default:
                    this.unexpected(token, context);
            }
        }
    }
    // command:
    //  operand (space operand)*
    //
    // space-separated arguments up to a pipeline character or right delimiter.
    // we consume the pipe character but leave the right delim to terminate the action.
    command() {
        const cmd = this.newCommand(this.peekNonSpace().pos);
        for (;;) {
            this.peekNonSpace(); // skip leading spaces.
            const operand = this.operand();
            if (operand) {
                cmd.Args.push(operand);
            }
            const token = this.next();
            switch (token.typ) {
                case lex_1.ItemType.itemSpace:
                    continue;
                case lex_1.ItemType.itemRightDelim:
                case lex_1.ItemType.itemRightParen:
                    this.backup();
                    break;
                case lex_1.ItemType.itemPipe:
                    // nothing here; break loop below
                    break;
                default:
                    this.unexpected(token, "operand");
            }
            break;
        }
        if (cmd.Args.length === 0) {
            this.errorf("empty command");
        }
        return cmd;
    }
    // operand:
    //  term .Field*
    //
    // An operand is a space-separated component of a command,
    // a term possibly followed by field accesses.
    // A nil return means the next item is not an operand.
    operand() {
        const node = this.term();
        if (!node) {
            return undefined;
        }
        if (this.peek().typ === lex_1.ItemType.itemField) {
            const chain = this.newChain(this.peek().pos, node);
            while (this.peek().typ === lex_1.ItemType.itemField) {
                const fieldVal = this.next().val;
                chain.add(fieldVal);
            }
            // Compatibility with original API: If the term is of type NodeField
            // or NodeVariable, just put more fields on the original.
            // Otherwise, keep the Chain node.
            // Obvious parsing errors involving literal values are detected here.
            // More complex error cases will have to be handled at execution time.
            switch (node.Type()) {
                case node_1.NodeType.NodeField:
                    return this.newField(chain.Position(), chain.String());
                case node_1.NodeType.NodeVariable:
                    const chainStr = chain.String();
                    return this.newVariable(chain.Position(), chainStr);
                case node_1.NodeType.NodeBool:
                case node_1.NodeType.NodeString:
                case node_1.NodeType.NodeNumber:
                case node_1.NodeType.NodeNil:
                case node_1.NodeType.NodeDot:
                    this.errorf("unexpected . after term %q", node.String());
                default:
                    return chain;
            }
        }
        return node;
    }
    // term:
    //  literal (number, string, nil, boolean)
    //  function (identifier)
    //  .
    //  .Field
    //  $
    //  '(' pipeline ')'
    //
    // A term is a simple "expression".
    // A nil return means the next item is not a term.
    term() {
        const token = this.nextNonSpace();
        if (!token) {
            return undefined;
        }
        switch (token.typ) {
            case lex_1.ItemType.itemIdentifier:
                const checkFunc = (this.Mode & Mode.SkipFuncCheck) === 0;
                if (checkFunc && !this.hasFunction(token.val)) {
                    this.errorf("function %q not defined", token.val);
                }
                return new node_1.IdentifierNode(token.val).setTree(this).setPos(token.pos);
            case lex_1.ItemType.itemDot:
                return this.newDot(token.pos);
            case lex_1.ItemType.itemNil:
                return this.newNil(token.pos);
            case lex_1.ItemType.itemVariable:
                return this.useVar(token.pos, token.val);
            case lex_1.ItemType.itemField:
                return this.newField(token.pos, token.val);
            case lex_1.ItemType.itemBool:
                return this.newBool(token.pos, token.val === "true");
            case lex_1.ItemType.itemCharConstant:
            case lex_1.ItemType.itemComplex:
            case lex_1.ItemType.itemNumber:
                const number = this.newNumber(token.pos, token.val, token.typ);
                if (number instanceof Error) {
                    this.error(number);
                }
                return number;
            case lex_1.ItemType.itemLeftParen:
                return this.pipeline("parenthesized pipeline", lex_1.ItemType.itemRightParen);
            case lex_1.ItemType.itemString:
            case lex_1.ItemType.itemRawString:
                const s = this.unquote(token.val);
                if (typeof s === "string") {
                    return this.newString(token.pos, token.val, s);
                }
                this.error(s);
        }
        // If we get here, the token is not a term
        this.backup();
        return undefined;
    }
    // checkPipeline checks the pipeline is well-formed.
    checkPipeline(pipe, context) {
        // Reject empty pipelines
        if (pipe.Cmds.length === 0) {
            this.errorf("missing value for %s", context);
        }
        // Only the first command of a pipeline can start with a non executable operand
        for (let i = 0; i < pipe.Cmds.length - 1; i++) {
            const cmd = pipe.Cmds[i + 1];
            if (!cmd.Args.length)
                continue;
            switch (cmd.Args[0].Type()) {
                case node_1.NodeType.NodeBool:
                case node_1.NodeType.NodeDot:
                case node_1.NodeType.NodeNil:
                case node_1.NodeType.NodeNumber:
                case node_1.NodeType.NodeString:
                    // With A|B|C, pipeline stage 2 is B
                    this.errorf("non executable command in pipeline stage %d", i + 2);
            }
        }
    }
    // useVar returns a node for a variable reference. It errors if the
    // variable is not defined.
    useVar(pos, name) {
        const v = this.newVariable(pos, name);
        for (const varName of this.vars) {
            if (varName === v.Ident[0]) {
                return v;
            }
        }
        this.errorf("undefined variable %q", v.Ident[0]);
        // This is unreachable because errorf() always throws
        throw new Error("unreachable");
    }
    // newVariable allocates a new variable with the given name.
    newVariable(pos, name) {
        // 分割名称并过滤掉空字符串，以保持与Go版本一致
        const parts = name.split(".").filter(p => p !== "");
        return new node_1.VariableNode(pos, parts).setTree(this);
    }
    // newField allocates a new field with the given name.
    newField(pos, name) {
        return new node_1.FieldNode(pos, name.substring(1).split(".")).setTree(this);
    }
    // newNumber allocates a new number node.
    newNumber(pos, text, typ) {
        const n = new node_1.NumberNode(this, pos, text);
        try {
            switch (typ) {
                case lex_1.ItemType.itemCharConstant: {
                    // 处理字符常量
                    if (text.length < 2 || text[0] !== "'" || text[text.length - 1] !== "'") {
                        throw new Error(`malformed character constant: ${text}`);
                    }
                    const char = text.slice(1, -1);
                    const rune = char.length === 1 ? char.charCodeAt(0) :
                        char.startsWith('\\u') ? parseInt(char.slice(2), 16) :
                            char.startsWith('\\x') ? parseInt(char.slice(2), 16) :
                                char.startsWith('\\') ? this.parseEscapeSequence(char) :
                                    char.charCodeAt(0);
                    n.IsInt = true;
                    n.Int64 = rune;
                    n.IsUint = true;
                    n.Uint64 = rune;
                    n.IsFloat = true;
                    n.Float64 = rune;
                    return n;
                }
                case lex_1.ItemType.itemComplex: {
                    // 处理复数
                    const match = text.match(/^([-+]?\d*\.?\d*(?:[eE][-+]?\d+)?)([-+]\d*\.?\d*(?:[eE][-+]?\d+)?)?i$/);
                    if (!match) {
                        throw new Error(`illegal complex number syntax: ${text}`);
                    }
                    const re = match[1] ? parseFloat(match[1]) : 0;
                    const im = match[2] ? parseFloat(match[2] + '1') : 1;
                    n.IsComplex = true;
                    n.Complex128 = { re, im };
                    this.simplifyComplex(n);
                    return n;
                }
            }
            // 处理可能的虚数常量
            if (text.endsWith('i')) {
                const im = parseFloat(text.slice(0, -1));
                if (!isNaN(im)) {
                    n.IsComplex = true;
                    n.Complex128 = { re: 0, im };
                    this.simplifyComplex(n);
                    return n;
                }
            }
            // 移除数字分隔符
            const cleanText = text.replace(/_/g, '');
            // 尝试解析为整数
            let value;
            let isInt = false;
            if (cleanText.startsWith('0x') || cleanText.startsWith('0X')) {
                value = parseInt(cleanText.slice(2), 16);
                isInt = true;
            }
            else if (cleanText.startsWith('0o') || cleanText.startsWith('0O')) {
                value = parseInt(cleanText.slice(2), 8);
                isInt = true;
            }
            else if (cleanText.startsWith('0b') || cleanText.startsWith('0B')) {
                value = parseInt(cleanText.slice(2), 2);
                isInt = true;
            }
            else if (cleanText.startsWith('0') && cleanText.length > 1 && !/[.eE]/.test(cleanText)) {
                // 处理八进制数字（以0开头）
                value = parseInt(cleanText, 8);
                isInt = true;
            }
            else {
                // 尝试解析为整数
                const intValue = parseInt(cleanText, 10);
                if (!isNaN(intValue) && intValue.toString() === cleanText) {
                    value = intValue;
                    isInt = true;
                }
                else {
                    // 尝试解析为浮点数
                    value = parseFloat(cleanText);
                    isInt = false;
                }
            }
            if (!isNaN(value)) {
                if (isInt) {
                    n.IsInt = true;
                    n.Int64 = value;
                    if (value >= 0) {
                        n.IsUint = true;
                        n.Uint64 = value;
                    }
                }
                n.IsFloat = true;
                n.Float64 = value;
                return n;
            }
            throw new Error(`illegal number syntax: ${text}`);
        }
        catch (e) {
            console.error(`[newNumber] Error creating number node: ${e}`);
            throw e instanceof Error ? e : new Error(String(e));
        }
    }
    simplifyComplex(n) {
        if (n.Complex128.im === 0) {
            n.IsFloat = true;
            n.Float64 = n.Complex128.re;
            const isInt = Number.isInteger(n.Float64);
            if (isInt) {
                n.IsInt = true;
                n.Int64 = n.Float64;
                if (n.Float64 >= 0) {
                    n.IsUint = true;
                    n.Uint64 = n.Float64;
                }
            }
        }
    }
    newBreak(pos, line) {
        return new node_1.BreakNode(pos, line).setTree(this);
    }
    newContinue(pos, line) {
        return new node_1.ContinueNode(pos, line).setTree(this);
    }
    newDot(pos) {
        return new node_1.DotNode(pos).setTree(this);
    }
    newNil(pos) {
        return new node_1.NilNode(pos).setTree(this);
    }
    newBool(pos, value) {
        return new node_1.BoolNode(pos, value).setTree(this);
    }
    newString(pos, quoted, text) {
        return new node_1.StringNode(pos, quoted, text).setTree(this);
    }
    newEnd(pos) {
        return new node_1.EndNode(pos).setTree(this);
    }
    newDefine(pos, line, name, pipe) {
        return new node_1.DefineNode(pos, line, name, pipe).setTree(this);
    }
    unquote(s) {
        if (s.length < 2) {
            return new Error("invalid quoted string");
        }
        const quote = s[0];
        if (quote !== s[s.length - 1]) {
            return new Error("invalid quoted string");
        }
        s = s.slice(1, -1);
        if (quote === '`') {
            return s;
        }
        let buf = '';
        for (let i = 0; i < s.length; i++) {
            if (s[i] !== '\\') {
                buf += s[i];
                continue;
            }
            if (i + 1 >= s.length) {
                return new Error("unterminated escape sequence");
            }
            i++;
            switch (s[i]) {
                case 'a':
                    buf += '\x07';
                    break;
                case 'b':
                    buf += '\b';
                    break;
                case 'f':
                    buf += '\f';
                    break;
                case 'n':
                    buf += '\n';
                    break;
                case 'r':
                    buf += '\r';
                    break;
                case 't':
                    buf += '\t';
                    break;
                case 'v':
                    buf += '\v';
                    break;
                case '\\':
                    buf += '\\';
                    break;
                case '"':
                    buf += '"';
                    break;
                case '\'':
                    buf += '\'';
                    break;
                default: return new Error(`invalid escape sequence: \\${s[i]}`);
            }
        }
        return buf;
    }
    // clearActionLine clears the action line
    clearActionLine() {
        this.actionLine = 0;
    }
    // add adds tree to t.treeSet.
    add() {
        const tree = this.treeSet.get(this.Name);
        if (!tree || this.isEmptyTree(tree.Root)) {
            this.treeSet.set(this.Name, this);
            return;
        }
        if (!this.isEmptyTree(this.Root)) {
            this.errorf("template: multiple definition of template %q", this.Name);
        }
    }
    // isEmptyTree reports whether this tree (node) is empty of everything but space or comments.
    isEmptyTree(node) {
        return IsEmptyTree(node);
    }
    // hasFunction reports if a function name exists in the Tree's maps.
    hasFunction(name) {
        for (const funcMap of this.funcs) {
            if (!funcMap) {
                continue;
            }
            if (funcMap.has(name)) {
                return true;
            }
        }
        return false;
    }
    // parseControl parses the control structures: if, while, range, with, etc.
    parseControl(allowElseIf, context) {
        // 保存当前变量数量，以便在函数结束时恢复
        const varCount = this.vars.length;
        try {
            // 解析管道
            const pipe = this.pipeline(context, lex_1.ItemType.itemRightDelim);
            // 如果是range，增加rangeDepth
            if (context === "range") {
                this.rangeDepth++;
            }
            // 解析项目列表
            let list;
            let next;
            [list, next] = this.itemList();
            // 如果是range，减少rangeDepth
            if (context === "range") {
                this.rangeDepth--;
            }
            // 处理结束或else
            let elseList = null;
            switch (next.Type()) {
                case node_1.NodeType.NodeEnd:
                    break;
                case node_1.NodeType.NodeElse:
                    if (allowElseIf) {
                        // 特殊处理"else if".
                        // 如果"else"后面紧跟着"if"，elseControl会保留"if"标记
                        // 将{{if a}}_{{else if b}}_{{end}}视为
                        // {{if a}}_{{else}}{{if b}}_{{end}}{{end}}
                        if (this.peek().typ === lex_1.ItemType.itemIf) {
                            this.next(); // 消耗"if"标记
                            elseList = this.newList(next.Position());
                            elseList.append(this.ifControl());
                            // 不消耗下一个项目 - 只需要一个{{end}}
                            break;
                        }
                    }
                    // 普通else
                    [elseList, next] = this.itemList();
                    if (next.Type() !== node_1.NodeType.NodeEnd) {
                        this.errorf("expected end; found %s", next);
                    }
                    break;
            }
            return [pipe.Position(), pipe.Line, pipe, list, elseList];
        }
        finally {
            // 恢复变量，与Go版本保持一致
            this.popVars(varCount);
        }
    }
    // parseEscapeSequence handles escape sequences without using eval.
    parseEscapeSequence(s) {
        if (s.length < 2 || s[0] !== '\\') {
            return s.charCodeAt(0);
        }
        switch (s[1]) {
            case 'a': return 0x07; // Bell
            case 'b': return 0x08; // Backspace
            case 'f': return 0x0C; // Form feed
            case 'n': return 0x0A; // Line feed
            case 'r': return 0x0D; // Carriage return
            case 't': return 0x09; // Horizontal tab
            case 'v': return 0x0B; // Vertical tab
            case '\\': return 0x5C; // Backslash
            case '\'': return 0x27; // Single quote
            case '"': return 0x22; // Double quote
            case '0': return 0x00; // Null character
            // Handle octal escapes \0, \00, \000
            case '1':
            case '2':
            case '3':
            case '4':
            case '5':
            case '6':
            case '7':
                let octalValue = parseInt(s.slice(1, Math.min(4, s.length)), 8);
                return octalValue;
            default:
                return s[1].charCodeAt(0);
        }
    }
}
exports.Tree = Tree;
// Parse returns a map from template name to parse.Tree, created by parsing the
// templates described in the argument string. The top-level template will be
// given the specified name. If an error is encountered, parsing stops and an
// empty map is returned with the error.
function parse(name, text, leftDelim = "", rightDelim = "", funcs = []) {
    const t = New(name, funcs);
    const treeSet = new Map();
    const [tree, err] = t.parse(text, leftDelim, rightDelim, treeSet, funcs);
    if (err) {
        console.error(`[parse] Error parsing template: ${err.message}`);
        return [new Map(), err];
    }
    // 将解析后的树添加到 treeSet 中
    treeSet.set(name, tree);
    return [treeSet, null];
}
// IsEmptyTree reports whether this tree (node) is empty of everything but space or comments.
function IsEmptyTree(node) {
    if (!node) {
        return true;
    }
    switch (node.Type()) {
        case node_1.NodeType.NodeAction:
            return false;
        case node_1.NodeType.NodeComment:
            return true;
        case node_1.NodeType.NodeIf:
            return false;
        case node_1.NodeType.NodeList:
            const listNode = node;
            for (const n of listNode.Nodes) {
                if (!IsEmptyTree(n)) {
                    return false;
                }
            }
            return true;
        case node_1.NodeType.NodeRange:
            return false;
        case node_1.NodeType.NodeTemplate:
            return false;
        case node_1.NodeType.NodeText:
            const textNode = node;
            return textNode.Text.length === 0 || new TextDecoder().decode(textNode.Text).trim() === "";
        case node_1.NodeType.NodeWith:
            return false;
        default:
            throw new Error("unknown node: " + node.String());
    }
}
//# sourceMappingURL=parse.js.map